"use client";

import React from "react";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight, Loader, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function LoginForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="bg-primary/10 border-primary/20 text-primary">
        <ShieldCheck className="size-5" />
        <AlertDescription className="text-base font-medium ml-2">
          {successMessage}
        </AlertDescription>
      </Alert>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="identifier" rules={{
      required: "Dieses Feld ist erforderlich",
      minLength: {
        value: 3,
        message: "Mindestens 3 Zeichen erforderlich"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">E-Mail oder Benutzername</FormLabel>
            <FormControl>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="size-4 text-muted-foreground" />
                </div>
                <Input placeholder="ihre.email@beispiel.de" className="pl-10 bg-background" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="password" rules={{
      required: "Passwort ist erforderlich",
      minLength: {
        value: 6,
        message: "Passwort muss mindestens 6 Zeichen lang sein"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel className="text-foreground">Passwort</FormLabel>
              <Link to="#" className="text-sm font-medium text-primary hover:underline">
                Passwort vergessen?
              </Link>
            </div>
            <FormControl>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="size-4 text-muted-foreground" />
                </div>
                <Input type="password" placeholder="••••••••" className="pl-10 bg-background" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium mt-2">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Wird angemeldet...
          </> : <>
            Anmelden
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function MemberLogin() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="memberlogin" className="relative min-h-screen flex items-center justify-center py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-muted/30 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/30 blur-[120px]" />
      </div>

      <div className="w-full max-w-md relative z-10" ref={ref}>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }}>
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img src="/img/ms_biotech_logo.png" alt="MS Biotechnology Logo" className="h-10 mx-auto" data-wvc-srcset="/img/ms_biotech_logo.png-wvc-srcset" data-wvc-sizes="/img/ms_biotech_logo.png-wvc-sizes" />
            </Link>
            <h1 className="text-3xl font-default font-bold text-foreground tracking-tight">
              Mitglieder-Login
            </h1>
            <p className="mt-3 text-muted-foreground text-sm max-w-sm mx-auto">
              Bitte melden Sie sich an, um auf das exklusive Partnerportal von MS Biotechnology zuzugreifen.
            </p>
          </div>

          <Card className="shadow-xl shadow-primary/5 border-border/50 backdrop-blur-sm bg-card/95">
            <CardContent className="pt-8 pb-8 px-6 sm:px-8">
              <FormProvider formId="member-login-form" sectionName="MemberLogin" defaultValues={{
              identifier: "",
              password: ""
            }} validationRules={{
              identifier: {
                required: true
              },
              password: {
                required: true
              }
            }} submitText="Anmelden" successMessage="Erfolgreich angemeldet. Sie werden in Kürze weitergeleitet." errorMessage="Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.">
                <LoginForm />
              </FormProvider>
            </CardContent>
            <CardFooter className="bg-muted/30 border-t border-border/50 px-6 py-4 flex justify-center">
              <p className="text-sm text-muted-foreground">
                Noch kein Partner?{" "}
                <Link to="#" className="font-medium text-primary hover:text-primary/80 transition-colors">
                  Kontaktieren Sie uns
                </Link>
              </p>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </section>;
}